/**
 * Finance / Budget Cost Overrides — reads/writes through the in-memory store.
 *
 * Storage:  src/Data/store.ts (in-memory Maps)
 * Durable:  PostgreSQL via API write-through
 *
 * NO localStorage reads or writes happen in this file.
 *
 * This file is a direct copy of scheduleProgressOverrides.ts with
 * field names changed for finance (baseline_budget, actual_spend, forecast_spend).
 */

import {
  batchUpsertBudgetSeriesPoints,
  type CreateBudgetSeriesPointRequest,
} from "../../api/budgetSeriesPoints";
import {
  upsertCurveMeta,
  type CreateCurveMetaRequest,
} from "../../api/curveMeta";
import {
  budgetSeriesStore,
  budgetMetaStore,
  financeLocalWriteTs,
  storeKey,
  isFinanceHydrated,
} from "../store";
import { broadcastFinanceUpdate } from "../../api/crossTabSync";

// ── Types ────────────────────────────────────────────────────

export type FinanceCostPoint = {
  project_guid: string;
  instance_guid?: string;
  reporting_date: string; // YYYY-MM-DD
  baseline_budget: number | null; // cumulative planned spend (currency units)
  actual_spend: number | null; // cumulative actual spend (currency units)
  forecast_spend: number | null; // cumulative forecast spend (currency units)
  /**
   * DEV-851: Submission timestamp from the API. Used by DataReliabilityTab
   * for per-row submission lateness — same rule as Schedule (`created_at -
   * reporting_date > grace` flips status to "late").
   */
  created_at?: string;
};

export type FinanceCurveType = "linear" | "front_loaded" | "back_loaded" | "sigmoidal" | "custom" | "copy" | "manual";
export type CostDirection = "increasing" | "decreasing";

export type ReportingCadence = "daily" | "weekly" | "monthly" | "quarterly";

export type ReportingSchedule = {
  cadence: ReportingCadence;
  interval?: number;
  by_weekday?: number[];
  due_time_local?: string;
  timezone?: string;
  until_date?: string;
};

export type BudgetCurveMeta = {
  project_guid?: string;
  instance_guid?: string;
  curve_type: FinanceCurveType;
  source_project_guid?: string;
  direction?: CostDirection;
  reporting_schedule?: ReportingSchedule;
  baseline_draft_id?: string;
  baseline_published_id?: string;
  baseline_published_version?: number;
  baseline_start_date?: string;
  baseline_end_date?: string;
  baseline_start_date_override?: string;
  baseline_end_date_override?: string;
  suppress_seed?: boolean;
  cleared_at?: string;
  total_budget?: number;
  currency?: string;
  /**
   * DEV-963: WBS code attached to the curve meta. Used by
   * BudgetThermometer for cost-breakdown reporting. Optional because
   * the API may not return it for older rows and the field is purely
   * informational (not used for math).
   */
  wbs_code?: string;
  /**
   * DEV-963: Optional role-slot GUID attached to the curve meta.
   * Persisted by BudgetThermometer when the cadence-due role slot is
   * set on the project. Same optionality rationale as wbs_code —
   * informational, not used for math.
   */
  assigned_role_slot_guid?: string;
};

// ── Internal helpers ─────────────────────────────────────────

function sortSeries(rows: FinanceCostPoint[]) {
  return [...rows].sort((a, b) => a.reporting_date.localeCompare(b.reporting_date));
}

function isISODate(s: any): boolean {
  return typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
}

function numOrNull(v: any): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Ensure store is warm (one-time localStorage migration if API hasn't loaded). */
function ensureWarm(): void {
  // No-op: localStorage warm-start removed. Data comes from API hydration only.
}

// ── Series reads ─────────────────────────────────────────────

export function getFinanceCostSeries(projectGuid: string): FinanceCostPoint[] {
  return getFinanceCostSeriesForInstance(projectGuid, undefined);
}

export function getFinanceCostSeriesForInstance(
  projectGuid: string,
  instanceGuid?: string | null,
): FinanceCostPoint[] {
  ensureWarm();
  const k = storeKey(projectGuid, instanceGuid);
  const data = budgetSeriesStore.get(k);
  if (Array.isArray(data) && data.length) return sortSeries(data);

  // Fallback: if instance-specific key not found, try project-level key.
  if (instanceGuid) {
    const projKey = storeKey(projectGuid, undefined);
    const projData = budgetSeriesStore.get(projKey);
    if (Array.isArray(projData) && projData.length) return sortSeries(projData);
  }

  return [];
}

/**
 * Return ALL finance cost points for a project, across all instance keys
 * (project-level + any instance-specific keys).
 */
export function getAllFinanceCostSeriesForProject(
  projectGuid: string,
): FinanceCostPoint[] {
  ensureWarm();
  const p = String(projectGuid || "").trim().toUpperCase();
  if (!p) return [];
  const combined: FinanceCostPoint[] = [];
  budgetSeriesStore.forEach((arr, key) => {
    if (key === p || key.startsWith(p + "::")) {
      if (Array.isArray(arr)) combined.push(...arr);
    }
  });
  return sortSeries(combined);
}

// ── Series writes ────────────────────────────────────────────

export function setFinanceCostSeries(projectGuid: string, rows: FinanceCostPoint[]) {
  setFinanceCostSeriesForInstance(projectGuid, undefined, rows);
}

export function setFinanceCostSeriesForInstance(
  projectGuid: string,
  instanceGuid: string | null | undefined,
  rows: FinanceCostPoint[],
  options?: { skipApiWrite?: boolean },
) {
  const p = String(projectGuid || "").trim();
  const k = storeKey(p, instanceGuid);
  const cleaned = sortSeries(
    (rows || [])
      .map((r) => ({
        project_guid: p,
        instance_guid: instanceGuid ? String(instanceGuid) : undefined,
        reporting_date: String((r as any).reporting_date || "").trim(),
        baseline_budget: numOrNull((r as any).baseline_budget),
        actual_spend: numOrNull((r as any).actual_spend),
        forecast_spend: numOrNull((r as any).forecast_spend),
      }))
      .filter((r) => isISODate(r.reporting_date))
  );

  // Update in-memory store + stamp local write time so hydration won't
  // overwrite this data during the grace window.
  budgetSeriesStore.set(k, cleaned);
  financeLocalWriteTs.set(k, Date.now());

  // Broadcast to other browser tabs via BroadcastChannel (instant, no API round-trip).
  broadcastFinanceUpdate(p, instanceGuid ? String(instanceGuid) : undefined, cleaned);

  // Signal React to re-render.
  try { window.dispatchEvent(new CustomEvent("l4e_finance_cost_changed")); } catch { /* ignore */ }

  // Write-through to API (fire-and-forget) — skip when caller handles API call.
  if (!options?.skipApiWrite && cleaned.length > 0) {
    const apiPoints: CreateBudgetSeriesPointRequest[] = cleaned.map((r) => ({
      projectId: r.project_guid,
      instanceGuid: r.instance_guid || undefined,
      reportingDate: r.reporting_date,
      baselineBudget: r.baseline_budget ?? undefined,
      actualSpend: r.actual_spend ?? undefined,
      forecastSpend: r.forecast_spend ?? undefined,
    }));
    batchUpsertBudgetSeriesPoints(apiPoints).catch((err) => {
      console.warn("[budgetOverrides] API write-through failed:", err?.message || err);
    });
  }
}

// ── Series clear ─────────────────────────────────────────────

export function clearFinanceCostSeries(projectGuid: string) {
  clearFinanceCostSeriesForInstance(projectGuid, undefined);
}

export function clearFinanceCostSeriesForInstance(
  projectGuid: string,
  instanceGuid?: string | null,
) {
  const k = storeKey(projectGuid, instanceGuid);
  budgetSeriesStore.delete(k);
  try { window.dispatchEvent(new CustomEvent("l4e_finance_cost_changed")); } catch { /* ignore */ }
}

// ── Curve metadata reads ─────────────────────────────────────

export function getBudgetCurveMeta(projectGuid: string): BudgetCurveMeta | null {
  return getBudgetCurveMetaForInstance(projectGuid, undefined);
}

export function getBudgetCurveMetaForInstance(
  projectGuid: string,
  instanceGuid?: string | null,
): BudgetCurveMeta | null {
  ensureWarm();
  const k = storeKey(projectGuid, instanceGuid);
  return budgetMetaStore.get(k) || null;
}

// ── Curve metadata writes ────────────────────────────────────

export function setBudgetCurveMeta(projectGuid: string, meta: BudgetCurveMeta) {
  setBudgetCurveMetaForInstance(projectGuid, undefined, meta);
}

export function setBudgetCurveMetaForInstance(
  projectGuid: string,
  instanceGuid: string | null | undefined,
  meta: BudgetCurveMeta,
) {
  const p = String(projectGuid || "").trim();
  const k = storeKey(p, instanceGuid);

  const cleaned: BudgetCurveMeta = {
    project_guid: p,
    instance_guid: instanceGuid ? String(instanceGuid) : undefined,
    curve_type: meta.curve_type,
    source_project_guid: meta.source_project_guid ? String(meta.source_project_guid) : undefined,
    direction: meta.direction === "decreasing" ? "decreasing" : "increasing",
    reporting_schedule: meta.reporting_schedule
      ? {
          cadence: meta.reporting_schedule.cadence,
          interval:
            typeof (meta.reporting_schedule as any).interval === "number" && Number.isFinite((meta.reporting_schedule as any).interval)
              ? (meta.reporting_schedule as any).interval
              : undefined,
          by_weekday: Array.isArray((meta.reporting_schedule as any).by_weekday)
            ? (meta.reporting_schedule as any).by_weekday.map((x: any) => Number(x)).filter((x: any) => Number.isFinite(x))
            : undefined,
          due_time_local: (meta.reporting_schedule as any).due_time_local ? String((meta.reporting_schedule as any).due_time_local) : undefined,
          timezone: (meta.reporting_schedule as any).timezone ? String((meta.reporting_schedule as any).timezone) : undefined,
          until_date: (meta.reporting_schedule as any).until_date ? String((meta.reporting_schedule as any).until_date) : undefined,
        }
      : undefined,
    baseline_draft_id: meta.baseline_draft_id ? String(meta.baseline_draft_id) : undefined,
    baseline_published_id: meta.baseline_published_id ? String(meta.baseline_published_id) : undefined,
    baseline_published_version:
      typeof meta.baseline_published_version === "number" && Number.isFinite(meta.baseline_published_version)
        ? meta.baseline_published_version
        : undefined,
    baseline_start_date: meta.baseline_start_date ? String(meta.baseline_start_date) : undefined,
    baseline_end_date: meta.baseline_end_date ? String(meta.baseline_end_date) : undefined,
    baseline_start_date_override: meta.baseline_start_date_override ? String(meta.baseline_start_date_override) : undefined,
    baseline_end_date_override: meta.baseline_end_date_override ? String(meta.baseline_end_date_override) : undefined,
    suppress_seed: !!(meta as any).suppress_seed,
    cleared_at: (meta as any).cleared_at ? String((meta as any).cleared_at) : undefined,
    total_budget:
      typeof meta.total_budget === "number" && Number.isFinite(meta.total_budget)
        ? meta.total_budget
        : undefined,
    currency: meta.currency ? String(meta.currency) : undefined,
  };

  // Update in-memory store.
  budgetMetaStore.set(k, cleaned);

  // Signal React.
  try { window.dispatchEvent(new CustomEvent("l4e_finance_cost_changed")); } catch { /* ignore */ }

  // Write-through to API.
  const metaPayload: CreateCurveMetaRequest = {
    projectId: p,
    instanceGuid: instanceGuid ? String(instanceGuid) : undefined,
    category: "finance",
    metaJson: cleaned,
  };
  upsertCurveMeta(metaPayload).catch((err) => {
    console.warn("[budgetOverrides] Curve meta API write-through failed:", err?.message || err);
  });
}

// ── Curve metadata clear ─────────────────────────────────────

export function clearBudgetCurveMeta(projectGuid: string) {
  clearBudgetCurveMetaForInstance(projectGuid, undefined);
}

export function clearBudgetCurveMetaForInstance(
  projectGuid: string,
  instanceGuid?: string | null,
) {
  const k = storeKey(projectGuid, instanceGuid);
  budgetMetaStore.delete(k);
  try { window.dispatchEvent(new CustomEvent("l4e_finance_cost_changed")); } catch { /* ignore */ }
}
